/**
 * ItemActionPopover + ItemActionTrigger
 *
 * A floating panel (popover) for item-level controls in diagram/layout elements:
 * fill color, text color, delete, and optional custom children. Triggered by the
 * companion ItemActionTrigger (3-dot button) rendered on each item on hover in
 * edit mode.
 *
 * Architecture notes:
 * - Portal-rendered via createPortal so it escapes any overflow:hidden containers.
 * - Positioned below the anchor (the trigger button) with an automatic flip to
 *   above when the popover would overflow the bottom of the viewport.
 * - Closes on click-outside and on Escape key.
 * - The floating rich-text toolbar (bold/italic/etc.) is managed separately by
 *   FloatingToolbarContainer at the slide level; this component handles
 *   ITEM-LEVEL controls only (colors, delete, children).
 *
 * Usage:
 * ```tsx
 * const triggerRef = useRef<HTMLButtonElement>(null);
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <ItemActionTrigger ref={triggerRef} onClick={() => setIsOpen(true)} />
 * <ItemActionPopover
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   anchorRef={triggerRef}
 *   shapeColor={step.shapeColor}
 *   onShapeColorChange={handleShapeColorChange}
 *   textColor={step.labelColor}
 *   onTextColorChange={handleTextColorChange}
 *   themeShapeColor={resolvedThemeShapeColor}
 *   themeTextColor={resolvedThemeTextColor}
 *   onDelete={handleDelete}
 *   deleteDisabled={isLastItem}
 * />
 * ```
 */

import React, { useEffect, useRef, useState, useCallback, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import ColorPicker from '../inputs/ColorPicker';

// ============================================================================
// TYPES
// ============================================================================

export interface ItemActionPopoverProps {
  /** Whether the popover is open. */
  isOpen: boolean;
  /** Called to close the popover. */
  onClose: () => void;
  /** The trigger button element used as the positioning anchor. */
  anchorRef: React.RefObject<HTMLElement>;

  /** Current fill (background) color of the item shape. */
  shapeColor?: string;
  /** Called when the user picks a new fill color. */
  onShapeColorChange?: (color: string) => void;

  /** Current text color of the item. */
  textColor?: string;
  /** Called when the user picks a new text color. */
  onTextColorChange?: (color: string) => void;

  /**
   * The resolved CSS color that the "theme" swatch should preview for the fill.
   * Passed through to ColorPicker's themeResolvedColor.
   */
  themeShapeColor?: string;
  /**
   * The resolved CSS color that the "theme" swatch should preview for the text.
   */
  themeTextColor?: string;

  /** Called when the user clicks the Delete button. */
  onDelete?: () => void;
  /** When true, the Delete button is rendered in a dimmed, non-interactive state. */
  deleteDisabled?: boolean;
  /**
   * Optional element-specific controls rendered between the color section and
   * the delete button (e.g., layout toggles, icon pickers).
   */
  children?: React.ReactNode;
}

export interface ItemActionTriggerProps {
  /** Called when the trigger button is clicked (after stopPropagation). */
  onClick: () => void;
  /** Additional className applied to the button element. */
  className?: string;
  /** Inline styles for positioning within the parent (e.g., absolute inset). */
  style?: React.CSSProperties;
}

// ============================================================================
// POSITIONING LOGIC
// ============================================================================

interface PopoverCoords {
  top: number;
  left: number;
}

const POPOVER_OFFSET_Y = 6; // gap between anchor bottom and popover top
const VIEWPORT_PADDING = 8;

/**
 * Calculate fixed-position coordinates for the popover, centered below the
 * anchor element. Flips above the anchor if the popover would overflow the
 * bottom viewport edge.
 */
function calculatePopoverPosition(
  anchorEl: HTMLElement,
  popoverEl: HTMLElement,
): PopoverCoords {
  const anchorRect = anchorEl.getBoundingClientRect();
  const popoverHeight = popoverEl.offsetHeight || 200;
  const popoverWidth = popoverEl.offsetWidth || 160;

  // Centered horizontally beneath the anchor
  let left = anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
  let top = anchorRect.bottom + POPOVER_OFFSET_Y;

  // Flip above the anchor if overflowing the bottom
  if (top + popoverHeight > window.innerHeight - VIEWPORT_PADDING) {
    top = anchorRect.top - popoverHeight - POPOVER_OFFSET_Y;
  }

  // Clamp horizontal to viewport
  left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - popoverWidth - VIEWPORT_PADDING));

  // Clamp vertical to viewport (fallback if flipping also overflows)
  top = Math.max(VIEWPORT_PADDING, top);

  return { top, left };
}

// ============================================================================
// ItemActionPopover
// ============================================================================

export const ItemActionPopover: React.FC<ItemActionPopoverProps> = ({
  isOpen,
  onClose,
  anchorRef,
  shapeColor,
  onShapeColorChange,
  textColor,
  onTextColorChange,
  themeShapeColor,
  themeTextColor,
  onDelete,
  deleteDisabled = false,
  children,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<PopoverCoords | null>(null);

  // ---- Position calculation ----

  const updatePosition = useCallback(() => {
    if (!anchorRef.current || !popoverRef.current) return;
    setCoords(calculatePopoverPosition(anchorRef.current, popoverRef.current));
  }, [anchorRef]);

  // Recalculate whenever open state or anchor changes
  useEffect(() => {
    if (!isOpen) return;

    // Initial measurement after the portal paints
    const frameId = requestAnimationFrame(updatePosition);

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // ---- Close on click-outside ----

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;

      // Ignore clicks inside the popover itself
      if (popoverRef.current?.contains(target)) return;

      // Ignore clicks on the trigger anchor (it manages open/close itself)
      if (anchorRef.current?.contains(target)) return;

      // Ignore clicks inside ColorPicker portals
      const colorPickerPortal = (target as HTMLElement).closest?.('[data-color-picker-portal]');
      if (colorPickerPortal) return;

      onClose();
    };

    // Delay registration so the triggering click doesn't immediately close the popover
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isOpen, onClose, anchorRef]);

  // ---- Close on Escape ----

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // ---- Render ----

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Item actions"
      className={cn(
        // Horizontal toolbar bar — matches the floating rich text toolbar look
        'fixed z-50',
        'bg-white rounded-lg shadow-lg',
        'px-2.5 py-1.5 flex items-center gap-1',
        'text-sm w-auto whitespace-nowrap',
        'min-h-[36px]',
      )}
      style={{
        ...(coords
          ? { top: coords.top, left: coords.left }
          : { top: -9999, left: -9999 }),
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Fill color picker */}
      {shapeColor !== undefined && onShapeColorChange !== undefined && (
        <ColorPicker
          value={shapeColor}
          onChange={onShapeColorChange}
          mode="popover"
          size="sm"
          showTransparent
          showThemeOption
          themeResolvedColor={themeShapeColor}
          label="Fill"
        />
      )}

      {/* Text color picker */}
      {textColor !== undefined && onTextColorChange !== undefined && (
        <ColorPicker
          value={textColor}
          onChange={onTextColorChange}
          mode="popover"
          size="sm"
          showTransparent={false}
          showThemeOption
          themeResolvedColor={themeTextColor}
          label="Text"
        />
      )}

      {/* Custom element-specific controls */}
      {children}

      {/* Delete button */}
      {onDelete !== undefined && (
        <>
          <div className="w-px h-5 bg-gray-200" />
          <button
            type="button"
            onClick={() => {
              if (!deleteDisabled) {
                onDelete();
                onClose();
              }
            }}
            disabled={deleteDisabled}
            className={cn(
              'p-1 rounded transition-colors',
              deleteDisabled
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-red-500 hover:bg-red-50',
            )}
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </div>,
    document.body,
  );
};

// ============================================================================
// ItemActionTrigger
// ============================================================================

/**
 * The 3-dot trigger button that opens the ItemActionPopover.
 * Rendered at the top-center of each item in edit mode on hover.
 * Uses forwardRef so the parent can pass the ref to ItemActionPopover as anchorRef.
 */
export const ItemActionTrigger = forwardRef<HTMLButtonElement, ItemActionTriggerProps>(
  ({ onClick, className, style }, ref) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick();
    };

    return (
      <button
        ref={ref}
        type="button"
        aria-label="Item actions"
        onClick={handleClick}
        className={cn(
          'flex items-center justify-center',
          'w-6 h-6 rounded-full',
          'bg-white/80 hover:bg-white',
          'shadow-sm',
          'transition-colors duration-100',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent-primary focus-visible:ring-offset-1',
          className,
        )}
        style={style}
      >
        <MoreHorizontal className="w-3.5 h-3.5 text-theme-text-secondary" />
      </button>
    );
  },
);

ItemActionTrigger.displayName = 'ItemActionTrigger';
